// @flow
import * as React from 'react';
import {useContext, useState} from 'react';
import type {Node} from 'react';
import DOMPurify from 'dompurify';
import Prism from 'prismjs';
import type {IntegrationBuilderType} from 'docs/data/location/Location';
import {parseFile, logError} from '../util';
import type {
  Step,
  Section,
  Files,
  Extensions,
  BuildFiles,
  BuildMethod,
  BuildFilesMethod,
  UpdateExtensionMethod,
  GetIntegrationDataMethod,
} from './GuideTypes';

const NEW_LINE_EXP = /\n(?!$)/g;

type ContentContextProps = {|
  sections: Array<Section>,
  extensions: Extensions,
  steps: Array<Step>,
  files: Files | {},
  docs: BuildFiles | {},
  copy: {
    title: string,
    description: string,
  },
  integrationType: IntegrationBuilderType,
  build: (props: BuildMethod) => void,
  buildFiles: (props: BuildFilesMethod) => {fileData: Files | {}},
  getIntegrationFiles: (props: GetIntegrationDataMethod) => Files | {},
  getStep: (id: string) => ?Step,
  updateExtension: (props: UpdateExtensionMethod) => Extensions,
  updateStep: (step: Step, tabId: string, file: string) => Step,
|};

const stubFunction = () => {
  throw new Error(
    'ContentContext function was called outside of ContentProvider scope.',
  );
};

const ContentContext = React.createContext<ContentContextProps>({
  sections: [],
  extensions: {},
  steps: [],
  files: {},
  docs: {},
  copy: {
    title: '',
    description: '',
  },
  integrationType: 'payments',
  build: stubFunction,
  buildFiles: stubFunction,
  getIntegrationFiles: stubFunction,
  getStep: stubFunction,
  updateExtension: stubFunction,
  updateStep: stubFunction,
});

type ContentProviderProps = {|
  integrations: any,
  sections: Array<Section>,
  children: Node,
  copy: {
    title: string,
    description: string,
  },
  integrationType: IntegrationBuilderType,
|};

export const ContentProvider = (props: ContentProviderProps) => {
  const [steps, setSteps] = useState([]);
  const [extensions, setExtensions] = useState({});
  const [files, setFiles] = useState({});
  const [docs, setDocs] = useState({});
  const integrations = props.integrations || {};
  const sections = props.sections || [];

  const getIntegrationFiles = ({client, server}: GetIntegrationDataMethod) => {
    let integrationFiles = {};
    if (client) {
      integrationFiles = integrations[client].files;
    }
    if (server && integrations[server]) {
      integrationFiles = {
        ...integrations[server].files,
        ...integrationFiles,
      };
    }
    return integrationFiles;
  };

  const buildSteps = ({client, server}) => {
    const sanitizeDescription = (description) => {
      return DOMPurify.sanitize(description);
    };

    // If the integration has a client then use the client steps as the base
    // narrative and merge the server steps in as overrides
    // Otherwise use the server steps if it's just a server integration (e.g. webhook)
    const baseSteps = client
      ? integrations[client].steps
      : integrations[server].steps;

    const formatStep = ({
      step,
      index,
      extensionId,
    }: {
      step: Step,
      index: number,
      extensionId?: string,
    }) => {
      if (step.file === 'server' && integrations[server]) {
        if (!(step.id in integrations[server].steps)) {
          return;
        }
        // Pull the right server step into the list
        const serverStep = integrations[server].steps[step.id];

        let bodyText;
        const rawBody = step.body || serverStep.body;
        if (typeof rawBody === 'string') {
          bodyText = sanitizeDescription(rawBody);
        } else {
          bodyText = rawBody;
        }

        return {
          ...step,
          ...integrations[server].steps[step.id],
          file: server,
          body: bodyText,
          server: true,
          index,
          extensionId,
        };
      } else {
        let bodyText;
        const rawBody = step.body;
        if (typeof rawBody === 'string') {
          bodyText = sanitizeDescription(rawBody);
        } else {
          bodyText = rawBody;
        }

        return {
          ...step,
          body: bodyText,
          index,
          extensionId,
        };
      }
    };

    const formattedSteps = [];

    // Create the list of steps
    let i = 0;
    baseSteps.forEach((step) => {
      let formattedStep = formatStep({step, index: i});
      if (formattedStep) {
        formattedSteps.push(formattedStep);
        i++;
      }
      if (step.steps) {
        step.steps.forEach((s) => {
          formattedStep = formatStep({
            step: s,
            index: i,
            extensionId: step.id,
          });
          if (formattedStep) {
            formattedSteps.push(formattedStep);
            i++;
          }
        });
      }
    });

    return formattedSteps.length ? formattedSteps : baseSteps;
  };

  /* Parses the file data and generates start / end lines for each step */
  const buildFiles = ({
    stepsData = steps,
    fileData = files,
    extensionData = extensions,
    options,
  }: BuildFilesMethod) => {
    Object.keys(fileData).forEach((filename) => {
      const file = fileData[filename];
      if (file.extension && !extensionData[file.extension]) {
        // In certain cases we have extra files that are only needed for
        // extensions, e.g. an extra PHP file for Charging a customer later
        // Do not include that file if the extension is not enabled
        delete fileData[filename];
        return;
      }

      if (file.filter && !file.filter(options)) {
        // Terminal has extra files for certain readers
        // Do not include that file if that reader is not selected
        delete fileData[filename];
        return;
      }
      // Parse the raw file into
      const {lines, spans, linesAdded} = parseFile({
        rawFile: file.builder({
          ...options,
          // This is a hack to dynamically change a line in the nodejs webhook content
          // $FlowFixMe -- need to strongly type extensionData and not have one-offs
          signedWebhook: extensionData.signature,
        }),
        filename,
        stepsData,
        extensionData,
      });

      // Format the new set of lines with Prism
      const code = lines.join('\n');
      const formattedCode = Prism.highlight(
        code,
        Prism.languages[file.language],
        file.language,
      );

      // Limit the allowed tags so we can safely set innerHTML
      const sanitizedCode = DOMPurify.sanitize(formattedCode);

      const linesList = sanitizedCode.split(NEW_LINE_EXP);

      // Add a span at the line at each index to wrap the content
      spans.forEach((span) => {
        const {index, content, value, type} = span;
        const line = linesList[index];
        if (line) {
          linesList[index] = line.replace(
            content,
            `<span class="token ${type}" data-value="${value}">${content}</span>`,
          );
        }
      });

      fileData[filename].lines = linesList;
      fileData[filename].raw = code;
      fileData[filename].linesAdded = linesAdded;
    });

    return {fileData};
  };

  /* Generates README and build files needed when downloading project */
  const buildDocs = ({client, server}): BuildFiles | {} => {
    if (!integrations.docs) {
      return {};
    }
    const {readme, buildInstructions} = integrations.docs;
    const buildInstructionFiles = buildInstructions(client, server);
    return {
      readme: {
        name: 'README.md',
        raw: readme(client, server),
      },
      ...buildInstructionFiles.files,
    };
  };

  /* Core builds method, in charge of generating files, steps, and docs */
  const build = ({
    languages,
    options,
    newExtensions = extensions,
  }: BuildMethod) => {
    const {client, server} = languages.active;
    if (!server) {
      // We don't have any IBs without server code
      logError(
        new Error(
          `Errors when loading files for ${String(client)} and ${String(
            server,
          )}`,
        ),
      );
      return;
    }
    const newFiles = getIntegrationFiles({client, server});
    const newSteps = buildSteps({client, server});
    const newDocs = buildDocs({client, server});

    const {fileData} = buildFiles({
      stepsData: newSteps,
      fileData: newFiles,
      extensionData: newExtensions,
      options,
    });

    setFiles(fileData);
    setSteps(newSteps);
    setDocs(newDocs);
    setExtensions(newExtensions);
  };

  const updateExtension = ({
    id,
    options,
    client,
    server,
  }: UpdateExtensionMethod) => {
    const extensionData = {...extensions};
    extensionData[id] = !extensions[id];
    let newFiles = {};
    if (client) {
      newFiles = integrations[client].files;
    }

    newFiles = {...newFiles, ...integrations[server].files};

    const {fileData} = buildFiles({fileData: newFiles, extensionData, options});
    setExtensions(extensionData);
    setFiles(fileData);
    return extensionData;
  };

  const updateStep = (step, tabId, file) => {
    const index = steps.findIndex((s) => s.id === step.id);
    if (!step.tabs) {
      return step;
    }

    const updatedStep = {
      ...step,
      file,
      lines: step.tabs.options[tabId].lines,
      tabs: {...step.tabs, active: tabId},
    };
    const updatedSteps = [
      ...steps.slice(0, index),
      updatedStep,
      ...steps.slice(index + 1),
    ];

    setSteps(updatedSteps);
    return updatedStep;
  };

  const value = {
    sections,
    extensions,
    steps,
    files,
    docs,
    copy: props.copy,
    integrationType: props.integrationType,
    build,
    buildFiles,
    getIntegrationFiles,
    getStep: (id) => steps.find((s) => s.id === id),
    updateExtension,
    updateStep,
  };

  return (
    <ContentContext.Provider value={value}>
      {props.children}
    </ContentContext.Provider>
  );
};

export const useContent = (): ContentContextProps => {
  return useContext(ContentContext);
};
